import React, { useState, useEffect, useRef } from 'react';
import { X, Target, Zap, Calendar, CheckCircle2, Circle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { getWeekNumber } from '@/utils';
import { Session } from '@supabase/supabase-js';

interface WeeklyMissionsModalProps {
    isOpen: boolean;
    onClose: () => void;
    session: Session;
}

export const WeeklyMissionsModal: React.FC<WeeklyMissionsModalProps> = ({ isOpen, onClose, session }) => {
    const [loading, setLoading] = useState(true);
    const [missions, setMissions] = useState([
        {
            id: 'mission:generate_5_links',
            title: 'Mestre da Otimização',
            description: 'Otimize 5 links ou mais durante a semana.',
            points: 1000,
            icon: <Zap className="w-5 h-5 text-fuchsia-500" />,
            completed: false
        },
        {
            id: 'mission:login_streak_7d_bonus',
            title: 'Presença Confirmada',
            description: 'Faça login todos os 7 dias da semana.',
            points: 840,
            icon: <Calendar className="w-5 h-5 text-blue-500" />,
            completed: false
        }
    ]);

    const hasFetched = useRef(false);

    useEffect(() => {
        if (isOpen && session?.user?.id && !hasFetched.current) {
            hasFetched.current = true;
            checkMissions();
        }
    }, [isOpen, session?.user?.id]);

    const checkMissions = async () => {
        setLoading(true);
        try {
            // Calculate current week key matching the backend logic
            const now = new Date();
            const brtString = now.toLocaleString("en-US", { timeZone: "America/Sao_Paulo" });
            const brtDate = new Date(brtString);
            const weekKey = `${brtDate.getFullYear()}-W${getWeekNumber(brtDate)}`;

            const { data, error } = await supabase
                .from('loyalty_user_actions')
                .select('reference_key')
                .eq('action', 'weekly_mission_completed')
                .eq('period_key', weekKey);

            if (data && !error) {
                const completedMissions = data.map(m => m.reference_key);

                setMissions(prevMissions => prevMissions.map(m => ({
                    ...m,
                    completed: completedMissions.includes(m.id)
                })));
            }
        } catch (error) {
            console.error('Error checking weekly missions:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    // Calculate time remaining until Sunday end of day (reset)
    const getDaysRemaining = () => {
        const now = new Date();
        const brtString = now.toLocaleString("en-US", { timeZone: "America/Sao_Paulo" });
        const brtDate = new Date(brtString);

        const currentDay = brtDate.getDay(); // 0 is Sunday, 1 is Monday...
        const daysUntilSunday = currentDay === 0 ? 0 : 7 - currentDay;

        return daysUntilSunday === 0 ? 'Termina hoje à noite' : `Faltam ${daysUntilSunday} dia(s)`;
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-white/20 dark:border-gray-800 overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]">

                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/50">
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-fuchsia-100 dark:bg-fuchsia-900/30 rounded-full text-fuchsia-600 dark:text-fuchsia-400 border border-fuchsia-200 dark:border-fuchsia-800">
                            <Target className="w-5 h-5" />
                        </div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Missões Semanais</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-800">
                    <div className="bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800/50 dark:to-gray-800/30 border border-gray-200 dark:border-gray-700/50 rounded-2xl p-4 mb-6 flex justify-between items-center text-sm">
                        <span className="text-gray-600 dark:text-gray-400 font-medium">Reset semanal:</span>
                        <span className="font-bold text-gray-900 dark:text-white bg-white dark:bg-gray-800 px-3 py-1 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
                            {getDaysRemaining()}
                        </span>
                    </div>

                    <div className="space-y-4">
                        {loading ? (
                            <div className="flex flex-col gap-4">
                                {[1, 2].map(i => (
                                    <div key={i} className="h-24 bg-gray-100 dark:bg-gray-800 rounded-2xl animate-pulse"></div>
                                ))}
                            </div>
                        ) : (
                            missions.map((mission, index) => (
                                <div
                                    key={index}
                                    className={`relative overflow-hidden p-4 rounded-2xl border transition-all ${mission.completed
                                        ? 'bg-emerald-50/50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800/50'
                                        : 'bg-white dark:bg-gray-800/50 border-gray-100 dark:border-gray-700 hover:border-fuchsia-200 dark:hover:border-fuchsia-900/50 shadow-sm hover:shadow-md'
                                        }`}
                                >
                                    {/* Completion background pulse effect */}
                                    {mission.completed && (
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 dark:bg-emerald-500/5 rounded-full blur-2xl -mt-10 -mr-10"></div>
                                    )}

                                    <div className="flex gap-4 relative z-10">
                                        <div className={`flex items-center justify-center flex-shrink-0 w-12 h-12 rounded-full mt-1 ${mission.completed
                                            ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400'
                                            : 'bg-gray-50 dark:bg-gray-900 shadow-inner border border-gray-100 dark:border-gray-700'
                                            }`}>
                                            {mission.completed ? <CheckCircle2 className="w-6 h-6" /> : mission.icon}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex justify-between items-start mb-1 gap-2">
                                                <h3 className={`text-sm font-bold ${mission.completed ? 'text-emerald-800 dark:text-emerald-300' : 'text-gray-900 dark:text-white'}`}>
                                                    {mission.title}
                                                </h3>
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black tracking-wider uppercase border ${mission.completed
                                                    ? 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800'
                                                    : 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800'
                                                    }`}>
                                                    +{mission.points} pts
                                                </span>
                                            </div>
                                            <p className={`text-xs leading-relaxed ${mission.completed ? 'text-emerald-600/80 dark:text-emerald-400/80' : 'text-gray-500 dark:text-gray-400'}`}>
                                                {mission.description}
                                            </p>

                                            <div className="mt-3 flex items-center gap-1.5">
                                                {mission.completed ? (
                                                    <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1 uppercase tracking-wider">
                                                        <CheckCircle2 className="w-3 h-3" /> Concluída
                                                    </span>
                                                ) : (
                                                    <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 flex items-center gap-1">
                                                        <Circle className="w-3 h-3" /> Em progresso
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};
